import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "fleet", label: t.nav.fleet },
    { id: "sales", label: t.nav.sales },
    { id: "contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    links.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const go = (id) => {
    setActive(id);
    setOpen(false);
    scrollToId(id);
  };

  const linkClass = (id) =>
    `text-sm tracking-wide transition-colors duration-300 ${
      active === id ? "text-[#60A5FA]" : "text-slate-300 hover:text-white"
    }`;

  const mobileLinkClass = (id) =>
    `text-left py-3 transition-colors border-b border-white/5 last:border-0 ${
      active === id ? "text-[#60A5FA]" : "text-slate-200 hover:text-[#60A5FA]"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0F172A]/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <img src="/logo-white.png" alt="LAJA Air Tech Services" className="h-11 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={linkClass(l.id)}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center text-xs font-mono-tech border border-white/15 rounded-full overflow-hidden" data-testid="lang-toggle">
            <button
              data-testid="lang-en-btn"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors duration-300 ${lang === "en" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}
            >
              EN
            </button>
            <button
              data-testid="lang-es-btn"
              onClick={() => setLang("es")}
              className={`px-3 py-1.5 transition-colors duration-300 ${lang === "es" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}
            >
              ES
            </button>
          </div>
          <button
            data-testid="nav-cta-btn"
            onClick={() => go("quote")}
            className="hidden md:inline-flex items-center px-5 py-2 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium transition-all duration-300 hover:scale-[1.03]"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-white/10 bg-[#020617]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className={mobileLinkClass(l.id)}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
